import type {
  ActivityDeletionBatchView,
  DeletionItemOutcome,
} from '@internal/spassu-code-contract';
import { Box, Button, Typography, makeStyles } from '@material-ui/core';
import DeleteSweepIcon from '@material-ui/icons/DeleteSweep';
import {
  BATCH_CONFIRMATION,
  BatchTargets,
  DestructiveConfirmDialog,
  OverrideConfirmation,
} from './AdminDeletionConfirmations';
import { useState } from 'react';

/** Human labels for the per-item outcomes the backend reports (ADR-015). */
export const DELETION_OUTCOME_LABELS: Record<DeletionItemOutcome, string> = {
  deleted: 'Excluída',
  cleanup_pending: 'Aguardando agente',
  rejected_completed: 'Protegida (concluída)',
  not_found: 'Não encontrada',
  conflict: 'Conflito',
};

const useStyles = makeStyles(theme => ({
  row: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  progress: {
    display: 'grid',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
  },
  warning: {
    color: theme.palette.error.main,
    fontWeight: 600,
  },
}));

export interface AdminDeletionPanelProps {
  /** Rendered only for an exact global administrator (ADR-005). */
  isGlobalAdmin: boolean;
  selectedIds: string[];
  /**
   * Title and repository for each selected id, so the confirmation can name the
   * exact targets. AGENTS.md: "Confirme alvos exatos antes de apagar ou
   * sobrescrever" — a bare count is not a confirmation of WHAT is being deleted,
   * and this deletion is irreversible and leaves no tombstone.
   */
  selectedTargets: Array<{
    id: string;
    title: string;
    repoName?: string | null;
  }>;
  busy: boolean;
  batch: ActivityDeletionBatchView | null;
  onRequestBatch(): void;
  onOverride(activityId: string): void;
  onClearSelection(): void;
}

/**
 * Shared shape for both destructive confirmations. Keeping one component makes
 * the two decisions look and behave alike without letting either inherit the
 * other's copy.
 */
/** Per-item progress with the override affordance for a pending device. */
function BatchProgress(props: {
  batch: ActivityDeletionBatchView;
  busy: boolean;
  onOverrideRequested(activityId: string): void;
}) {
  const classes = useStyles();
  const pendingCount = props.batch.items.filter(
    item => item.outcome === 'cleanup_pending',
  ).length;
  return (
    <Box className={classes.progress} data-testid="deletion-batch-progress">
      <Typography variant="overline" component="h3">
        Progresso da exclusão
      </Typography>
      {props.batch.items.map(item => (
        <Box key={item.activityId} className={classes.row}>
          <Typography variant="body2">
            {`${item.activityId}: ${DELETION_OUTCOME_LABELS[item.outcome]}`}
          </Typography>
          {item.outcome === 'cleanup_pending' ? (
            <Button
              size="small"
              color="secondary"
              onClick={() => props.onOverrideRequested(item.activityId)}
              disabled={props.busy}
              aria-label={`Ignorar exclusão local de ${item.activityId}`}
            >
              Ignorar exclusão local
            </Button>
          ) : null}
        </Box>
      ))}
      {pendingCount === 0 ? (
        <Typography variant="body2" role="status">
          Nenhum item aguardando agente.
        </Typography>
      ) : null}
    </Box>
  );
}

/** Selection count plus the two selection-scoped actions. */
function SelectionToolbar(props: {
  selectedCount: number;
  busy: boolean;
  onRequest(): void;
  onClearSelection(): void;
}) {
  const classes = useStyles();
  return (
    <Box className={classes.row}>
      <Button
        size="small"
        startIcon={<DeleteSweepIcon fontSize="small" />}
        onClick={props.onRequest}
        disabled={props.busy || props.selectedCount === 0}
        aria-label="Excluir atividades selecionadas"
      >
        {`Excluir selecionadas (${props.selectedCount})`}
      </Button>
      {props.selectedCount > 0 ? (
        <Button
          size="small"
          onClick={props.onClearSelection}
          disabled={props.busy}
        >
          Limpar seleção
        </Button>
      ) : null}
    </Box>
  );
}

/**
 * Administrative deletion controls.
 *
 * Two separate confirmations by design (ADR-005/ADR-015): one to start the batch,
 * and a distinct one per item to ignore a pending local cleanup. Override is an
 * individual decision — it is never a property of the batch — and cancellation is
 * never offered here at all, because a cancellation requires its own reason
 * (UT-029).
 */
export function AdminDeletionPanel(props: AdminDeletionPanelProps) {
  const [confirmBatch, setConfirmBatch] = useState(false);
  const [overrideTarget, setOverrideTarget] = useState<string | null>(null);
  if (!props.isGlobalAdmin) return null;
  return (
    <Box>
      <SelectionToolbar
        selectedCount={props.selectedIds.length}
        busy={props.busy}
        onRequest={() => setConfirmBatch(true)}
        onClearSelection={props.onClearSelection}
      />

      {props.batch ? (
        <BatchProgress
          batch={props.batch}
          busy={props.busy}
          onOverrideRequested={setOverrideTarget}
        />
      ) : null}

      <DestructiveConfirmDialog
        {...BATCH_CONFIRMATION}
        open={confirmBatch}
        busy={props.busy}
        onClose={() => setConfirmBatch(false)}
        onConfirm={() => {
          setConfirmBatch(false);
          props.onRequestBatch();
        }}
      >
        <BatchTargets targets={props.selectedTargets} />
      </DestructiveConfirmDialog>

      <OverrideConfirmation
        activityId={overrideTarget}
        targets={props.selectedTargets}
        items={props.batch?.items}
        busy={props.busy}
        onClose={() => setOverrideTarget(null)}
        onConfirm={activityId => {
          setOverrideTarget(null);
          props.onOverride(activityId);
        }}
      />
    </Box>
  );
}
